import React from 'react';
import { X, ArrowLeft, Users, Target, Heart, Zap, Award } from 'lucide-react';

export default function AboutUs({ onClose }) {
    const team = [
        {
            name: "Dr. Sarah Chen",
            role: "Founder & CEO",
            bio: "Former AI researcher at MIT. 15+ years pioneering multi-modal intelligence systems.",
            emoji: "👩‍💼"
        },
        {
            name: "Marcus Rodriguez",
            role: "Chief Technology Officer",
            bio: "Ex-Google Brain. Led development of ambient AI architectures for Fortune 500 companies.",
            emoji: "👨‍💻"
        },
        {
            name: "Aisha Patel",
            role: "Head of Strategy",
            bio: "McKinsey alum. Specialized in AI transformation and organizational redesign.",
            emoji: "👩‍🔬"
        },
        {
            name: "James Liu",
            role: "Lead AI Architect",
            bio: "PhD in Cognitive Science. Designed the Nonary Lattice integration framework.",
            emoji: "👨‍🎓"
        }
    ];

    const values = [
        {
            icon: <Target size={32} />,
            title: "Vision-Driven",
            description: "We don't build for today's problems. We architect for tomorrow's possibilities."
        },
        {
            icon: <Heart size={32} />,
            title: "Human-Centric",
            description: "Technology should amplify humanity, not replace it. Every solution puts people first."
        },
        {
            icon: <Zap size={32} />,
            title: "Bold Innovation",
            description: "We challenge assumptions and reimagine what's possible with AI."
        },
        {
            icon: <Award size={32} />,
            title: "Excellence",
            description: "We deliver world-class solutions backed by rigorous research and proven methodologies."
        }
    ];

    const milestones = [
        { year: "2021", event: "Deep 9 founded with a vision to reimagine AI integration" },
        { year: "2022", event: "Developed the Nonary Lattice framework" },
        { year: "2023", event: "First enterprise deployment of a synthetic sapience system" },
        { year: "2024", event: "Launched Sapix, our flagship platform for agents" },
        { year: "2025", event: "Expanded to 50+ clients across 12 industries" }
    ];

    return (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-10">
                <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-gray-700 hover:text-amber-600 transition"
                    >
                        <ArrowLeft size={20} />
                        <span className="font-medium">Back</span>
                    </button>
                    <button
                        onClick={onClose}
                        className="text-gray-700 hover:text-amber-600 transition"
                    >
                        <X size={24} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-6 py-16">
                {/* Hero */}
                <div className="mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full border border-amber-300 mb-6">
                        <Users size={16} className="text-amber-600" />
                        <span className="text-amber-900 text-sm font-semibold">About Deep 9</span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Building the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Future of Intelligence</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                        Deep 9 was born from a simple but profound realization: the AI revolution isn't about making existing systems smarter—it's about fundamentally reimagining how intelligence shapes our world. We build the platforms that make that shift real—starting with Sapix.
                    </p>
                </div>

                {/* Our Story */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 border border-gray-200">
                        <p className="text-lg text-gray-700 leading-relaxed mb-4">
                            In 2021, our founders came together with a shared frustration: every AI implementation they encountered was merely retrofitting old systems with new capabilities. Companies were adding chatbots to websites, automating repetitive tasks, and calling it "transformation."
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed mb-4">
                            But true transformation requires more than incremental improvements. It demands a complete reimagination of how we live, work, and create value in a world where intelligence is ambient.
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            Deep 9 was founded to bridge this gap—to help organizations move beyond retrofitting and embrace the full potential of AI-native thinking. We take our name from the Nonary Lattice: the nine dimensions of intelligence a system needs to reason like a partner rather than a tool. Today, we're proud to be at the forefront of the Synthetic Sapience revolution, delivering it through Sapix and the platforms that follow.
                        </p>
                    </div>
                </section>

                {/* Our Values */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Values</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {values.map((value, idx) => (
                            <div key={idx} className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-amber-300 hover:shadow-lg transition">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center text-amber-600 mb-4">
                                    {value.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                                <p className="text-gray-700 leading-relaxed">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Our Team */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Meet Our Team</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {team.map((member, idx) => (
                            <div key={idx} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 hover:border-amber-300 transition">
                                <div className="flex items-start gap-4">
                                    <div className="text-5xl">{member.emoji}</div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                                        <p className="text-amber-600 font-semibold mb-3">{member.role}</p>
                                        <p className="text-gray-700 leading-relaxed">{member.bio}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Our Journey */}
                <section className="mb-16">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Our Journey</h2>
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 md:p-12 border border-amber-200">
                        <div className="space-y-6">
                            {milestones.map((milestone, idx) => (
                                <div key={idx} className="flex gap-6 items-start">
                                    <div className="flex-shrink-0">
                                        <div className="w-20 h-20 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl flex items-center justify-center">
                                            <span className="text-white font-bold text-lg">{milestone.year}</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 pt-4">
                                        <p className="text-lg text-gray-900 font-medium">{milestone.event}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Stats */}
                <section className="mb-16">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-600 mb-2">50+</div>
                            <p className="text-gray-600">Enterprise Clients</p>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-600 mb-2">12</div>
                            <p className="text-gray-600">Industries Served</p>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-600 mb-2">9</div>
                            <p className="text-gray-600">Intelligence Types</p>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-600 mb-2">100%</div>
                            <p className="text-gray-600">Client Satisfaction</p>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="text-center bg-gradient-to-r from-amber-600 via-orange-600 to-amber-600 rounded-3xl p-12 text-white">
                    <h2 className="text-3xl font-bold mb-4">Join Us on This Journey</h2>
                    <p className="text-xl text-amber-50 mb-8 max-w-2xl mx-auto">
                        We're always looking for visionary partners and talented individuals who share our passion for reimagining the future.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button className="px-8 py-4 bg-white text-amber-600 rounded-xl font-bold text-lg hover:shadow-2xl transition">
                            Work With Us
                        </button>
                        <button className="px-8 py-4 bg-white/20 text-white rounded-xl font-bold text-lg hover:bg-white/30 transition border border-white/30">
                            Join Our Team
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
}
